/**
 * useMasterEntry.js
 * -----------------
 * Industry-standard reusable headless React hook for master create/edit forms.
 * 
 * Stability & Performance Architecture:
 * 1. Uses useRef for method references and callbacks so they never cause dependency churn.
 * 2. Automated record loading triggers strictly on editingId changes.
 * 3. Works seamlessly with any field schema (1 field, 2 fields, foreign keys, text areas).
 */
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { appToast } from '../../../components/AppToast';

export function useMasterEntry({
  entityName = 'Record',
  apiClient = null,
  fetchDetail = null,
  createItem = null,
  updateItem = null,
  initialForm = {},
  validate = null,
  listPath = '/masters',
  transformPayload = null,
  transformDetail = null,
}) {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const [searchParams] = useSearchParams();
  const editingId = paramId || searchParams.get('id');


  const [form, setForm] = useState(initialForm);
  const [operationalStatus, setOperationalStatus] = useState('active'); // 'active' | 'inactive'
  const [isLoading, setIsLoading] = useState(Boolean(editingId));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Keep method references in refs
  const fetchDetailRef = useRef(fetchDetail);
  fetchDetailRef.current = fetchDetail;
  const createItemRef = useRef(createItem);
  createItemRef.current = createItem;
  const updateItemRef = useRef(updateItem);
  updateItemRef.current = updateItem;
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const validateRef = useRef(validate);
  validateRef.current = validate;
  const transformPayloadRef = useRef(transformPayload);
  transformPayloadRef.current = transformPayload;
  const transformDetailRef = useRef(transformDetail);
  transformDetailRef.current = transformDetail;

  const getDetailFn = useCallback(() => {
    if (typeof fetchDetailRef.current === 'function') return fetchDetailRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['getById', `get${entityName}Detail`, `get${entityName}`, 'get'];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') return target[name].bind(target);
      }
    }
    return null;
  }, [entityName]);

  const getCreateFn = useCallback(() => {
    if (typeof createItemRef.current === 'function') return createItemRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['create', `create${entityName}`, 'post'];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') return target[name].bind(target);
      }
    }
    return null;
  }, [entityName]);

  const getUpdateFn = useCallback(() => {
    if (typeof updateItemRef.current === 'function') return updateItemRef.current;
    const client = apiClientRef.current;
    if (!client) return null;
    const targets = [client, client.default, client.client].filter(Boolean);
    const candidateNames = ['update', `update${entityName}`, 'put'];
    for (const target of targets) {
      for (const name of candidateNames) {
        if (typeof target[name] === 'function') return target[name].bind(target);
      }
    }
    return null;
  }, [entityName]);

  // Load existing item strictly when editingId changes
  useEffect(() => {
    async function loadIfEditing() {
      if (!editingId) return;
      const detailFn = getDetailFn();
      if (!detailFn) {
        const err = `[useMasterEntry] Could not resolve detail method for ${entityName}.`;
        console.error(err, { apiClient: apiClientRef.current, fetchDetail: fetchDetailRef.current });
        setError(err);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError('');
      try {
        const detail = await detailFn(editingId);
        if (detail) {
          if (transformDetailRef.current) {
            const transformed = transformDetailRef.current(detail);
            if (transformed.form) setForm(transformed.form);
            if (transformed.operationalStatus) setOperationalStatus(transformed.operationalStatus);
          } else {
            const nextForm = { ...initialForm };
            Object.keys(initialForm).forEach((key) => {
              if (detail[key] !== undefined) {
                nextForm[key] = detail[key] ?? '';
              }
            });
            setForm(nextForm);
            setOperationalStatus(detail.status === 'Active' ? 'active' : 'inactive');
          }
        } else {
          setError(`${entityName} not found.`);
        }
      } catch (e) {
        console.error(`[useMasterEntry] Error loading ${entityName} detail:`, e);
        const msg = e?.response?.data?.detail || e?.message || `Failed to load ${entityName.toLowerCase()} details.`;
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      } finally {
        setIsLoading(false);
      }
    }
    loadIfEditing();
  }, [editingId, entityName, getDetailFn]);

  const patchForm = (fieldOrObject, value) => {
    if (typeof fieldOrObject === 'object' && fieldOrObject !== null) {
      setForm((prev) => ({ ...prev, ...fieldOrObject }));
    } else {
      setForm((prev) => ({ ...prev, [fieldOrObject]: value }));
    }
  };

  const handleCancel = () => {
    navigate(listPath);
  };

  const handleSave = async () => {
    if (validateRef.current) {
      const validationError = validateRef.current(form);
      if (validationError) {
        setError(validationError);
        appToast.error(validationError);
        return;
      }
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = transformPayloadRef.current
        ? transformPayloadRef.current(form, operationalStatus)
        : {
            ...form,
            status: operationalStatus === 'active' ? 'Active' : 'Archive',
          };

      if (editingId) {
        const updateFn = getUpdateFn();
        if (!updateFn) throw new Error(`Update function not resolved for ${entityName}`);
        await updateFn(editingId, payload);
        const msg = `${entityName} updated successfully.`;
        appToast.success(msg);
        setSuccess(msg);
      } else {
        const createFn = getCreateFn();
        if (!createFn) throw new Error(`Create function not resolved for ${entityName}`);
        await createFn(payload);
        const msg = `${entityName} created successfully.`;
        appToast.success(msg);
        setSuccess(msg);
      }

      setTimeout(() => {
        navigate(listPath);
      }, 600);
    } catch (e) {
      console.error(`[useMasterEntry] Error saving ${entityName}:`, e);
      const msg = e?.response?.data?.detail || e?.message || `Failed to save ${entityName.toLowerCase()}.`;
      const errStr = typeof msg === 'string' ? msg : JSON.stringify(msg);
      setError(errStr);
      appToast.error(errStr);
    } finally {
      setIsSaving(false);
    }
  };

  const completionPercent = useMemo(() => {
    const keys = Object.keys(initialForm);
    if (!keys.length) return 100;
    const filled = keys.filter((key) => String(form[key] || '').trim().length > 0).length;
    return Math.round((filled / keys.length) * 100);
  }, [form, initialForm]);

  return {
    state: {
      form,
      operationalStatus,
      isLoading,
      isSaving,
      error,
      success,
      isEditMode: Boolean(editingId),
      completionPercent,
    },
    actions: {
      patchForm,
      setOperationalStatus,
      handleSave,
      handleCancel,
    },
  };
}
